import React, { useState, useEffect } from 'react';
import { 
  getSystemStats,
  SystemStats
} from '../../services/dashboardService';
import { formatDistance, formatTime, formatPace, formatSpeed } from '../../utils/formatters';
import { 
  FaUsers, 
  FaRunning, 
  FaTrophy, 
  FaSync, 
  FaCalendarAlt, 
  FaChartLine, 
  FaMedal,
  FaRoad,
  FaClock,
  FaMountain,
  FaTachometerAlt,
  FaStopwatch,
  FaUserFriends
} from 'react-icons/fa';
import './dashboard.css';

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<SystemStats | null>(null);
  
  // Định nghĩa biến mặc định cho filter ngày
  const defaultStartDateLocal = "2023-01-01T00:00:00";
  const defaultEndDate = "2025-12-31T23:59:59";

  // Hàm lấy dữ liệu dashboard từ API
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Lấy thống kê hệ thống
      const systemStats = await getSystemStats(defaultStartDateLocal, defaultEndDate);
      setStats(systemStats);
    } catch (err: any) {
      console.error('Lỗi khi lấy dữ liệu dashboard:', err);
      setError(err.message || 'Không thể lấy dữ liệu. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };
  
  // Gọi API khi component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Chuyển đổi mảng ngày thành đối tượng Date
  const formatDateFromArray = (dateArray: number[] | undefined): string => {
    if (!dateArray || dateArray.length < 3) return 'N/A';
    
    // Lấy các thành phần ngày từ mảng [year, month, day, hour, minute, second]
    const year = dateArray[0];
    const month = dateArray[1] - 1; // Tháng trong JS là 0-11
    const day = dateArray[2];
    
    // Thêm thời gian nếu có
    const hour = dateArray[3] || 0;
    const minute = dateArray[4] || 0;
    const second = dateArray[5] || 0;
    
    const date = new Date(year, month, day, hour, minute, second);
    return date.toLocaleDateString('vi-VN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Render thông tin khi đang tải
  if (loading) {
  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h2 className="dashboard-title">
            <span className="dashboard-title-icon"><FaChartLine /></span> Bảng điều khiển
        </h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner">
            <FaSync className="spin-animation" />
          </div>
          <p>Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }
  
  // Render thông báo lỗi
  if (error) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-header">
          <h2 className="dashboard-title">
            <span className="dashboard-title-icon"><FaChartLine /></span> Bảng điều khiển
          </h2>
        </div>
        <div className="alert alert-danger">
          <i className="alert-icon">!</i>
          <div className="alert-content">{error}</div>
        </div>
        <button className="btn btn-primary" onClick={fetchDashboardData}>
          <FaSync /> Thử lại
        </button>
      </div>
    );
  }
  
  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h2 className="dashboard-title">
          <span className="dashboard-title-icon"><FaChartLine /></span> Bảng điều khiển
        </h2>
            <button 
              className={`btn-refresh ${loading ? 'loading' : ''}`}
              onClick={fetchDashboardData}
              disabled={loading}
              title="Làm mới dữ liệu"
            >
              <FaSync />
            </button>
          </div>
          
      {/* Thống kê tổng quan */}
      <div className="stats-section">
        <div className="section-header">
          <h3 className="section-title"><span><FaChartLine /></span> Thống kê tổng quan</h3>
        </div>
        
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FaUsers />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.userCount || 0}</div>
              <div className="stat-label">Tổng người dùng</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <FaUserFriends />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.teamCount || 0}</div>
              <div className="stat-label">Tổng đội</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <FaTrophy />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.tournamentCount || 0}</div>
              <div className="stat-label">Tổng giải đấu</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <FaRunning />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.activityCount || 0}</div>
              <div className="stat-label">Tổng hoạt động</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Thống kê hoạt động */}
      <div className="stats-section">
        <div className="section-header">
          <h3 className="section-title"><span><FaRunning /></span> Thống kê hoạt động</h3>
        </div>
        
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FaRoad />
            </div>
            <div className="stat-content">
              <div className="stat-value">{formatDistance(stats?.totalDistance || 0)}</div>
              <div className="stat-label">Tổng quãng đường</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <FaClock />
            </div>
            <div className="stat-content">
              <div className="stat-value">{formatTime(stats?.totalMovingTime || 0)}</div>
              <div className="stat-label">Tổng thời gian di chuyển</div>
        </div>
      </div>
      
          <div className="stat-card">
            <div className="stat-icon">
              <FaTachometerAlt />
        </div>
            <div className="stat-content">
              <div className="stat-value">{formatSpeed(stats?.averageSpeed || 0)}</div>
              <div className="stat-label">Tốc độ trung bình</div>
        </div>
      </div>
      
          <div className="stat-card">
            <div className="stat-icon">
              <FaStopwatch />
        </div>
            <div className="stat-content">
              <div className="stat-value">{formatPace(stats?.averagePace || 0)}</div>
              <div className="stat-label">Pace trung bình</div>
        </div>
      </div>
      
          <div className="stat-card">
            <div className="stat-icon">
              <FaMountain />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.totalElevationGain || 0}m</div>
              <div className="stat-label">Tổng độ cao đã leo</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <FaCalendarAlt />
              </div>
            <div className="stat-content">
              <div className="stat-value">{formatDateFromArray(stats?.lastActivityDate)}</div>
              <div className="stat-label">Hoạt động gần nhất</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Top người dùng */}
      {stats?.topUser && (
        <div className="top-entities-section">
          <div className="top-entities-grid">
            <div className="top-entity-card">
              <div className="top-entity-header">
                <h3 className="top-entity-title">
                  <FaMedal /> Người dùng xuất sắc nhất
                </h3>
              </div>
              <div className="top-entity-body">
                <div className="top-user-info">
                  <img 
                    src={stats.topUser.avatarUrl || "/assets/default-avatar.png"} 
                    alt={stats.topUser.fullName}
                    className="top-user-avatar"
                  />
                  <div className="top-user-details">
                    <div className="top-user-name">{stats.topUser.fullName}</div>
                  </div>
                </div>
                <div className="top-entity-stats">
                  <div className="top-entity-stat">
                    <div className="top-entity-stat-label">
                      <FaRoad /> Quãng đường
                    </div>
                    <div className="top-entity-stat-value">
                      {formatDistance(stats.topUser.totalDistance)}
                    </div>
                  </div>
                  <div className="top-entity-stat">
                    <div className="top-entity-stat-label">
                      <FaRunning /> Hoạt động
                    </div>
                    <div className="top-entity-stat-value">
                      {stats.topUser.activityCount}
                    </div>
                  </div>
                  <div className="top-entity-stat">
                    <div className="top-entity-stat-label">
                      <FaClock /> Thời gian
                    </div>
                    <div className="top-entity-stat-value">
                      {formatTime(stats.topUser.totalMovingTime)}
                    </div>
                  </div>
                  <div className="top-entity-stat">
                    <div className="top-entity-stat-label">
                      <FaTachometerAlt /> Tốc độ TB
                    </div>
                    <div className="top-entity-stat-value">
                      {formatSpeed(stats.topUser.averageSpeed)}
                    </div>
                  </div>
                </div>
        </div>
            </div>
          
            {/* Top đội */}
            {stats.topTeam && (
              <div className="top-entity-card">
                <div className="top-entity-header">
                  <h3 className="top-entity-title">
                    <FaUserFriends /> Đội xuất sắc nhất
                  </h3>
                </div>
                <div className="top-entity-body">
                  <div className="top-team-info">
                    <div className="top-team-name">{stats.topTeam.teamName}</div>
                    <div className="top-team-tournament">
                      <FaTrophy style={{ marginRight: '5px' }} />
                      {stats.topTeam.tournamentName}
                    </div>
                  </div>
                  <div className="top-entity-stats">
                    <div className="top-entity-stat">
                      <div className="top-entity-stat-label">
                        <FaRoad /> Quãng đường
                      </div>
                      <div className="top-entity-stat-value">
                        {formatDistance(stats.topTeam.totalDistance)}
                      </div>
                    </div>
                    <div className="top-entity-stat">
                      <div className="top-entity-stat-label">
                        <FaRunning /> Hoạt động
                      </div>
                      <div className="top-entity-stat-value">
                        {stats.topTeam.activityCount}
                      </div>
                    </div>
                    <div className="top-entity-stat">
                      <div className="top-entity-stat-label">
                        <FaUsers /> Thành viên
                      </div>
                      <div className="top-entity-stat-value">
                        {stats.topTeam.userCount}
                      </div>
                    </div>
                    <div className="top-entity-stat">
                      <div className="top-entity-stat-label">
                        <FaClock /> Thời gian
                      </div>
                      <div className="top-entity-stat-value">
                        {formatTime(stats.topTeam.totalMovingTime)}
                      </div>
                    </div>
                </div>
                </div>
              </div>
            )}
          
            {/* Top giải đấu */}
            {stats.topTournament && (
              <div className="top-entity-card">
                <div className="top-entity-header">
                  <h3 className="top-entity-title">
                    <FaTrophy /> Giải đấu nổi bật
                  </h3>
                </div>
                <div className="top-entity-body">
                  <div className="top-tournament-info">
                    <div className="top-tournament-name">{stats.topTournament.tournamentName}</div>
                    <div className="top-tournament-date">
                      <FaCalendarAlt style={{ marginRight: '5px' }} />
                      {formatDateFromArray(stats.topTournament.startDate)} - {formatDateFromArray(stats.topTournament.endDate)}
                    </div>
                    <div className="top-tournament-description">
                      {stats.topTournament.description}
                    </div>
                  </div>
                  <div className="top-entity-stats">
                    <div className="top-entity-stat">
                      <div className="top-entity-stat-label">
                        <FaUserFriends /> Đội
                      </div>
                      <div className="top-entity-stat-value">
                        {stats.topTournament.teamCount}
                      </div>
                    </div>
                    <div className="top-entity-stat">
                      <div className="top-entity-stat-label">
                        <FaUsers /> Người tham gia
                      </div>
                      <div className="top-entity-stat-value">
                        {stats.topTournament.userCount}
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default Dashboard; 